/** @jsxImportSource theme-ui */
import React from 'react';

import theme from 'theme';
import { formatCaseCountPrevalence } from 'utils/stringFormattingHelpers';

import { VariantLiteral } from 'components/_common/Typography/Typography';

import { Typography } from '../../Typography';
import BdCaseIncidenceInheritedIcon from './BdCaseIncidenceInheritedIcon';

const getPerCapitaLabel = (timeframe?: string) => {
  return timeframe ? `Cases per 100,000 ${timeframe}` : 'Cases per 100,000';
};

type BdCaseIncidenceIconProps = {
  caseCountPrevalence: number;
  /** Displays the case incidence level if true, emdash and grey if false */
  isCaseDataAvailable: boolean;
  /** Adds an asterisk if the data is inherited */
  isCaseDataInherited?: boolean;
  /** True if this is on a dark background */
  dark?: boolean;
  /** A string that is used to display "in last XX days" */
  timeframe?: string;
  /** Icon only */
  simple?: boolean;
  /** The Cases per 100k label position */
  labelPosition?: 'bottom' | 'top';
  /** The font variant of the cases per 100k label */
  labelVariant?: VariantLiteral;
  /** The font variant of the data point */
  dataLabelVariant?: VariantLiteral;
};

export const BdCaseIncidenceIcon: React.FC<BdCaseIncidenceIconProps> = ({
  caseCountPrevalence,
  isCaseDataAvailable = false,
  isCaseDataInherited = false,
  dark = false,
  timeframe = null,
  simple = false,
  labelPosition = 'bottom',
  labelVariant = 'caption',
  dataLabelVariant = 'subtitle2',
}) => {
  if (!isCaseDataAvailable) {
    return (
      <NoCasesReportedIcon
        dark={dark}
        timeframe={timeframe}
        showLabel={!simple}
        labelPosition={labelPosition}
        labelVariant={labelVariant}
        dataLabelVariant={dataLabelVariant}
      />
    );
  }

  return (
    <WithReportedCasesIcon
      caseCountPrevalence={caseCountPrevalence}
      isCaseDataInherited={isCaseDataInherited}
      dark={dark}
      timeframe={timeframe}
      showLabel={!simple}
      labelPosition={labelPosition}
      labelVariant={labelVariant}
      dataLabelVariant={dataLabelVariant}
    />
  );
};

type NoCasesReportedIconProps = {
  dark: boolean;
  showLabel: boolean;
  timeframe: string;
  labelPosition: 'bottom' | 'top';
  labelVariant: VariantLiteral;
  dataLabelVariant: VariantLiteral;
};
const NoCasesReportedIcon: React.FC<NoCasesReportedIconProps> = ({
  dark,
  showLabel,
  timeframe,
  labelPosition,
  labelVariant,
  dataLabelVariant,
}) => {
  const circleColour = theme.colors.stone50;
  const iconColour = dark ? theme.colors.stone20 : theme.colors.stone50;
  const strokeColour = dark ? theme.colors.stone20 : theme.colors.stone70;

  const noDataAvailableSvg = (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g opacity="0.5">
        <circle opacity="0.15" cx="18" cy="18" r="12" fill={circleColour} />
        <path
          d="M17.9993 17.6549C17.3701 17.6549 16.755 17.4682 16.2319 17.1185C15.7088 16.7688 15.3013 16.2718 15.0608 15.6903C14.8203 15.1089 14.7577 14.4691 14.8809 13.8521C15.0042 13.235 15.3077 12.6684 15.7531 12.224C16.1985 11.7795 16.7657 11.4772 17.383 11.3552C18.0003 11.2333 18.6399 11.2972 19.2209 11.5389C19.8018 11.7806 20.298 12.1892 20.6466 12.7131C20.9953 13.2369 21.1807 13.8524 21.1793 14.4816C21.1776 15.3238 20.8418 16.131 20.2456 16.7259C19.6494 17.3208 18.8416 17.6549 17.9993 17.6549ZM17.9993 12.0683C17.522 12.0683 17.0554 12.2098 16.6586 12.475C16.2617 12.7402 15.9524 13.1171 15.7697 13.558C15.587 13.999 15.5392 14.4843 15.6324 14.9524C15.7255 15.4205 15.9553 15.8506 16.2928 16.1881C16.6304 16.5256 17.0604 16.7554 17.5285 16.8486C17.9967 16.9417 18.4819 16.8939 18.9229 16.7112C19.3639 16.5286 19.7408 16.2192 20.0059 15.8224C20.2711 15.4255 20.4127 14.9589 20.4127 14.4816C20.4109 13.8421 20.1561 13.2293 19.7039 12.777C19.2517 12.3248 18.6388 12.07 17.9993 12.0683Z"
          fill={iconColour}
          stroke={strokeColour}
          strokeWidth="0.3"
        />
        <path
          d="M23.3328 23.8396H12.6661C12.5654 23.8396 12.4687 23.7996 12.3974 23.7283C12.3262 23.6571 12.2861 23.5604 12.2861 23.4596C12.2861 20.0596 14.8328 17.293 17.9728 17.293C21.1128 17.293 23.6595 20.0596 23.6595 23.4596C23.6604 23.5518 23.6277 23.6412 23.5676 23.7111C23.5075 23.781 23.4241 23.8267 23.3328 23.8396ZM13.0861 23.073H22.9128C22.7328 20.2796 20.5995 18.0596 17.9995 18.0596C15.3995 18.0596 13.2661 20.2796 13.0861 23.073Z"
          fill={iconColour}
          stroke={strokeColour}
          strokeWidth="0.3"
        />
      </g>
      <line x1="9.64645" y1="26.6464" x2="26.6464" y2="9.64645" stroke={strokeColour} />
    </svg>
  );
  return (
    <div
      sx={{
        display: 'flex',
      }}
    >
      <div sx={{ display: 'flex', justifyContent: 'center' }}>{noDataAvailableSvg}</div>
      {showLabel && (
        <div
          sx={{
            display: 'flex',
            flexDirection: labelPosition === 'bottom' ? 'column' : 'column-reverse',
            ml: '8px',
          }}
        >
          <Typography variant={dataLabelVariant} color={dark ? 'stone10' : 'stone90'}>
            —
          </Typography>
          <Typography variant={labelVariant} color={dark ? 'deepSea40' : 'stone50'}>
            {getPerCapitaLabel(timeframe)}
          </Typography>
        </div>
      )}
    </div>
  );
};

type WithReportedCasesIconProps = {
  caseCountPrevalence: number;
  isCaseDataInherited: boolean;
  dark: boolean;
  timeframe: string;
  showLabel: boolean;
  labelPosition: 'bottom' | 'top';
  labelVariant: VariantLiteral;
  dataLabelVariant: VariantLiteral;
};
const WithReportedCasesIcon: React.FC<WithReportedCasesIconProps> = ({
  caseCountPrevalence,
  isCaseDataInherited,
  dark = false,
  timeframe,
  showLabel,
  labelPosition,
  labelVariant,
  dataLabelVariant,
}) => {
  const circleColour = theme.colors.starfish100;
  const iconColour = dark ? theme.colors.starfish40 : theme.colors.starfish100;

  const badgeSvg = (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle opacity="0.15" cx="18" cy="18" r="15" fill={circleColour} />
      <path
        d="M17.9993 17.6549C17.3701 17.6549 16.755 17.4682 16.2319 17.1185C15.7088 16.7688 15.3013 16.2718 15.0608 15.6903C14.8203 15.1089 14.7577 14.4691 14.8809 13.8521C15.0042 13.235 15.3077 12.6684 15.7531 12.224C16.1985 11.7795 16.7657 11.4772 17.383 11.3552C18.0003 11.2333 18.6399 11.2972 19.2209 11.5389C19.8018 11.7806 20.298 12.1892 20.6466 12.7131C20.9953 13.2369 21.1807 13.8524 21.1793 14.4816C21.1776 15.3238 20.8418 16.131 20.2456 16.7259C19.6494 17.3208 18.8416 17.6549 17.9993 17.6549ZM17.9993 12.0683C17.522 12.0683 17.0554 12.2098 16.6586 12.475C16.2617 12.7402 15.9524 13.1171 15.7697 13.558C15.587 13.999 15.5392 14.4843 15.6324 14.9524C15.7255 15.4205 15.9553 15.8506 16.2928 16.1881C16.6304 16.5256 17.0604 16.7554 17.5285 16.8486C17.9967 16.9417 18.4819 16.8939 18.9229 16.7112C19.3639 16.5286 19.7408 16.2192 20.0059 15.8224C20.2711 15.4255 20.4127 14.9589 20.4127 14.4816C20.4109 13.8421 20.1561 13.2293 19.7039 12.777C19.2517 12.3248 18.6388 12.07 17.9993 12.0683Z"
        fill={iconColour}
        stroke={iconColour}
        strokeWidth="0.3"
      />
      <path
        d="M23.3328 23.8396H12.6661C12.5654 23.8396 12.4687 23.7996 12.3974 23.7283C12.3262 23.6571 12.2861 23.5604 12.2861 23.4596C12.2861 20.0596 14.8328 17.293 17.9728 17.293C21.1128 17.293 23.6595 20.0596 23.6595 23.4596C23.6604 23.5518 23.6277 23.6412 23.5676 23.7111C23.5075 23.781 23.4241 23.8267 23.3328 23.8396ZM13.0861 23.073H22.9128C22.7328 20.2796 20.5995 18.0596 17.9995 18.0596C15.3995 18.0596 13.2661 20.2796 13.0861 23.073Z"
        fill={iconColour}
        stroke={iconColour}
        strokeWidth="0.3"
      />
    </svg>
  );

  return (
    <div data-testid="case-incidence-has-data-icon">
      <div
        sx={{
          display: 'flex',
        }}
      >
        <div sx={{ display: 'flex', justifyContent: 'center' }}>{badgeSvg}</div>
        {showLabel && (
          <div
            sx={{
              display: 'flex',
              flexDirection: labelPosition === 'bottom' ? 'column' : 'column-reverse',
              ml: '8px',
            }}
          >
            <Typography
              variant={dataLabelVariant}
              color={dark ? 'stone10' : 'stone90'}
              marginBottom={labelPosition === 'bottom' ? '4px' : '0'}
            >
              {formatCaseCountPrevalence(caseCountPrevalence)}
              {isCaseDataInherited && <BdCaseIncidenceInheritedIcon />}
            </Typography>
            <Typography
              variant={labelVariant}
              color={dark ? 'deepSea40' : 'stone70'}
              marginBottom={labelPosition === 'bottom' ? '0' : '4px'}
            >
              {getPerCapitaLabel(timeframe)}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default BdCaseIncidenceIcon;
